/**
 * Refactored AiTableOfContent component using extracted hooks, components, and modals
 */

import React, { useState, useRef } from 'react'
import { Modal, message } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import uuidv4 from 'uuid/v4'
import history from '../../../history'

// Import custom hooks
import {
  useContentGeneration,
  useContentManagement,
  useSummarizeContent
} from './hooks'

// Import utility functions
import {
  mapObjectByOrder,
  downloadDocx,
  downloadWithoutReferences
} from './utils'

// Import components
import { ContentHeader, ContentSection, ActionButtons } from './components'

// Import modals
import {
  AddTopicModal,
  AddLessonModal,
  ConfirmationModal,
  DocumentModal,
  GeneratedContentModal,
  SummarizeModal,
  PreviewAllModal,
  SummarizedContentModal,
  ApplyAllConfirmModal,
  ImagesModal
} from './modals'

// Import other components
import AiTemplateSelection from '../AiTemplateSelection/AiTemplateSelection'

import './AiTableOfContent.scss'
const TableOfContents = ({
  courseTitle,
  tableOfContent,
  onTableOfContentChange,
  onContentGenerationStarted,
  isFromKnowledgeCenter,
  courseId: aiCourseId,
  setIsContentGenerationCompleted,
  files = [],
  language
}) => {
  const { t } = useTranslation()
  const user = useSelector(state => state.user)
  const courseId = isFromKnowledgeCenter ? uuidv4() : aiCourseId

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [newLessonModalVisible, setNewLessonModalVisible] = useState(false)
  const [newTopicModalVisible, setNewTopicModalVisible] = useState(false)
  const [newLessonTitle, setNewLessonTitle] = useState('')
  const [currentSection, setCurrentSection] = useState(null)
  const [currentSubsection, setCurrentSubsection] = useState(null)
  const [newTitle, setNewTitle] = useState('')
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(
    false
  )
  const [editTopicIndex, setEditTopicIndex] = useState(-1)
  const [editTopicTopIndex, setEditTopicTopIndex] = useState(-1)
  const [editTopicStatus, setEditTopicStatus] = useState(false)
  const [generatedContentModal, setGeneratedContentModal] = useState({
    isVisible: false,
    content: '',
    title: '',
    lesson: ''
  })
  const [showDocumentModal, setShowDocumentModal] = useState(false)
  const [openTemplateModal, setOpenTemplateModal] = useState(false)
  const [imagesModal, setImagesModal] = useState({
    isVisible: false,
    imgArray: []
  })

  const [contentChoiceModal, setContentChoiceModal] = useState({
    isVisible: false,
    content: ''
  })

  // Use custom hooks
  const {
    content,
    setContent,
    updateContent,
    isAddingContentFromInternet,
    setIsAddingContentFromInternet,
    handleDelete,
    handleAddTopic,
    handleAddLesson,
    handleSaveEdit,
    handleAddContentFromInternet,
    progressStatus,
    isContentGeneratedSuccessfully,
    contentSuccessInAllSections,
    checkError
  } = useContentManagement({
    tableOfContent,
    onTableOfContentChange,
    isFromKnowledgeCenter,
    language
  })

  const {
    isGeneratingContent,
    setIsGeneratingContent,
    isGeneratingCourse,
    setIsGeneratingCourse,
    isSavingContent,
    setIsSavingContent,
    isAutoUpdate,
    setIsAutoUpdate,
    saveContentToDatabase,
    handleGenerateCourseContent,
    handleCreateCourse
  } = useContentGeneration({
    content,
    setContent,
    courseTitle,
    courseId,
    user,
    onContentGenerationStarted,
    setIsContentGenerationCompleted,
    contentChoice: files,
    language
  })

  const {
    summarizeModal,
    setSummarizeModal,
    summarizedContent,
    setSummarizedContent,
    isApplyingAll,
    setIsApplyingAll,
    previewAllModal,
    setPreviewAllModal,
    isApplyAllConfirmVisible,
    setIsApplyAllConfirmVisible,
    handleSummarizeContent,
    handleSummarizeSubmit,
    handleUseSummarizedContent,
    handleApplyToAll,
    handleApplyAllPreview,
    handleCancelSummarize,
    handleApplyAllFromSummarized
  } = useSummarizeContent({ language })

  // Event handlers
  const showNewLessonModal = () => {
    setNewLessonModalVisible(true)
  }

  const handleEdit = (section, subsection = null, index, tindex, wstatus) => {
    setCurrentSection(section)
    setCurrentSubsection(subsection)
    setNewTitle(subsection ? subsection : section)
    setIsModalVisible(true)
    setEditTopicIndex(index)
    setEditTopicTopIndex(tindex)
    setEditTopicStatus(wstatus)
  }

  const handleSaveEditClick = () => {
    handleSaveEdit(currentSection, currentSubsection, newTitle)
    setIsModalVisible(false)
    setEditTopicIndex(-1)
    setEditTopicTopIndex(-1)
    setEditTopicStatus(false)
  }

  const handleCancelEdit = () => {
    setIsModalVisible(false)
    setEditTopicIndex(-1)
    setEditTopicTopIndex(-1)
    setEditTopicStatus(false)
  }

  const handleAddTopicClick = () => {
    handleAddTopic(currentSection, newTitle)
    setNewTitle('')
    setNewTopicModalVisible(false)
  }

  const handleAddLessonClick = () => {
    handleAddLesson(newLessonTitle)
    setNewLessonTitle('')
    setNewLessonModalVisible(false)
  }

  const handleShowImages = array => {
    setImagesModal({
      isVisible: true,
      imgArray: array
    })
  }

  const onSortTopicsEnd = ({ sectionTitle, oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return
    const newContent = { ...content }
    const section = newContent[sectionTitle]
    const newTopics = mapObjectByOrder(section, oldIndex, newIndex)
    newContent[sectionTitle] = newTopics
    updateContent(newContent)
  }

  const handleViewContent = (lesson, title, content, references) => {
    console.log({ content })
    setGeneratedContentModal({
      isVisible: true,
      content: content,
      lesson: lesson,
      title: title,
      references: references
    })
  }

  const handleSaveGeneratedContent = () => {
    setIsAutoUpdate(true)
    const newContent = { ...content }
    const {
      content: generatedContent,
      title: subTopic,
      lesson
    } = generatedContentModal
    newContent[lesson][subTopic].generatedContent = generatedContent
    setContent(newContent)
    setGeneratedContentModal({
      isVisible: false,
      content: '',
      title: '',
      lesson: '',
      references: ''
    })
  }

  const handleSummarizeContentClick = () => {
    handleSummarizeContent()
  }

  const handleSummarizeSubmitClick = () => {
    handleSummarizeSubmit(generatedContentModal)
  }

  const handleUseSummarizedContentClick = () => {
    handleUseSummarizedContent(setGeneratedContentModal)
  }

  const handleApplyToAllClick = () => {
    const instructions = summarizeModal && summarizeModal.instructions
    if (!instructions || instructions.trim().length === 0) {
      message.error(t('v5:please_enter_editing_instruction'))
      return
    }
    setIsApplyAllConfirmVisible(true)
  }

  const handleApplyAllConfirmClick = () => {
    setIsApplyAllConfirmVisible(false)
    handleApplyToAll(content)
  }

  const handleApplyAllPreviewClick = () => {
    handleApplyAllPreview(
      updateContent,
      saveContentToDatabase,
      setGeneratedContentModal
    )
  }

  const handleApplyAllFromSummarizedClick = () => {
    handleApplyAllFromSummarized(content, generatedContentModal)
  }

  const handleAddContentFromInternetClick = () => {
    handleAddContentFromInternet(
      generatedContentModal,
      courseTitle,
      setGeneratedContentModal
    )
  }

  const confirmCreateCourse = templateData => {
    Modal.confirm({
      title: t('v4:confirm_creating_course'),
      content: t('v4:generate_content_automatically'),
      onOk() {
        handleCreateCourse(templateData)
      },
      centered: true,
      width: '500',
      okText: t('v4:ok'),
      cancelText: t('v4:cancel')
    })
  }

  const handleContentChoice = value => {
    setContentChoiceModal({ content: value, isVisible: false })
    setIsConfirmationModalVisible(true)
  }

  const toggleDocumentModal = () => setShowDocumentModal(!showDocumentModal)

  const showInternetContentLoading =
    isAddingContentFromInternet &&
    generatedContentModal.lesson === isAddingContentFromInternet.topic &&
    generatedContentModal.title === isAddingContentFromInternet.sub_topic

  return (
    <div className="ai-course__table-of-content">
      <div className="ai-course-table-of-content-inner">
        <ContentHeader
          isGeneratingContent={isGeneratingContent}
          progressStatus={progressStatus}
        />

        <ContentSection
          content={content}
          isModalVisible={isModalVisible}
          editTopicStatus={editTopicStatus}
          editTopicIndex={editTopicIndex}
          editTopicTopIndex={editTopicTopIndex}
          newTitle={newTitle}
          onNewTitleChange={e => setNewTitle(e.target.value)}
          onEdit={handleEdit}
          onSaveEdit={handleSaveEditClick}
          onCancelEdit={handleCancelEdit}
          onDelete={handleDelete}
          onViewContent={handleViewContent}
          onShowImages={handleShowImages}
          onSortTopicsEnd={onSortTopicsEnd}
          onAddTopic={sectionTitle => {
            setCurrentSection(sectionTitle)
            setNewTopicModalVisible(true)
          }}
          isGeneratingContent={isGeneratingContent}
        />

        <ActionButtons
          contentSuccessInAllSections={contentSuccessInAllSections}
          isGeneratingContent={isGeneratingContent}
          isFromKnowledgeCenter={isFromKnowledgeCenter}
          isSavingContent={isSavingContent}
          isGeneratingCourse={isGeneratingCourse}
          progressStatus={progressStatus}
          checkError={checkError}
          onShowNewLessonModal={showNewLessonModal}
          onSaveContent={() => saveContentToDatabase()}
          onToggleDocumentModal={toggleDocumentModal}
          onOpenTemplateModal={() => setOpenTemplateModal(true)}
          // onGenerateContent={() => setContentChoiceModal({ isVisible: true, content: '' })}
          onGenerateContent={() => setIsConfirmationModalVisible(true)}
        />
      </div>

      {/* Modals */}
      <AiTemplateSelection
        open={openTemplateModal}
        isGeneratingCourse={isGeneratingCourse}
        close={() => setOpenTemplateModal(false)}
        onConfirm={confirmCreateCourse}
        courseId={aiCourseId}
      />

      <AddTopicModal
        visible={newTopicModalVisible}
        onOk={handleAddTopicClick}
        onCancel={() => setNewTopicModalVisible(false)}
        value={newTitle}
        onChange={e => setNewTitle(e.target.value)}
      />

      <AddLessonModal
        visible={newLessonModalVisible}
        onOk={handleAddLessonClick}
        onCancel={() => setNewLessonModalVisible(false)}
        value={newLessonTitle}
        onChange={e => setNewLessonTitle(e.target.value)}
      />

      <ConfirmationModal
        visible={isConfirmationModalVisible}
        onOk={() => {
          handleGenerateCourseContent()
          setIsConfirmationModalVisible(false)
        }}
        onCancel={() => setIsConfirmationModalVisible(false)}
      />

      <DocumentModal
        visible={showDocumentModal}
        onCancel={toggleDocumentModal}
        onDownloadWithRefs={() => downloadDocx(content, courseTitle)}
        onDownloadWithoutRefs={() =>
          downloadWithoutReferences(content, courseTitle)
        }
      />

      <GeneratedContentModal
        visible={generatedContentModal.isVisible}
        onCancel={() => {
          if (showInternetContentLoading) return
          setIsAutoUpdate(true)
          setGeneratedContentModal({
            isVisible: false,
            content: '',
            title: '',
            lesson: '',
            references: ''
          })
        }}
        title={generatedContentModal.title}
        content={generatedContentModal.content}
        references={generatedContentModal.references}
        onContentChange={value =>
          setGeneratedContentModal(prev => ({
            ...prev,
            content: value
          }))
        }
        onSave={handleSaveGeneratedContent}
        onSummarize={handleSummarizeContentClick}
        onAddContentFromInternet={handleAddContentFromInternetClick}
        showInternetContentLoading={showInternetContentLoading}
      />

      <SummarizeModal
        visible={summarizeModal.isVisible}
        onOk={handleSummarizeSubmitClick}
        onCancel={handleCancelSummarize}
        onApplyToAll={handleApplyToAllClick}
        instructions={summarizeModal.instructions}
        onInstructionsChange={e => {
          const value = e.target ? e.target.value : ''
          setSummarizeModal(prev => ({
            ...prev,
            instructions: value
          }))
        }}
        isSummarizing={summarizeModal.isSummarizing}
        isApplyingAll={isApplyingAll}
        content={generatedContentModal.content}
        onContentChange={value =>
          setGeneratedContentModal(prev => ({
            ...prev,
            content: value
          }))
        }
      />

      <PreviewAllModal
        visible={previewAllModal.isVisible}
        onCancel={() =>
          setPreviewAllModal({ isVisible: false, stagedContent: null })
        }
        onApply={handleApplyAllPreviewClick}
        stagedContent={previewAllModal.stagedContent}
        onStagedContentChange={updated =>
          setPreviewAllModal(prev => ({
            ...prev,
            stagedContent: updated
          }))
        }
        onUndo={() => {
          // Close preview modal and summarization modal, keep generated content modal visible
          setPreviewAllModal({ isVisible: false, stagedContent: null })
          setSummarizeModal({
            isVisible: false,
            instructions: '',
            isSummarizing: false
          })
          setSummarizedContent('')
          setGeneratedContentModal(prev => ({ ...prev, isVisible: true }))
        }}
      />

      <SummarizedContentModal
        visible={summarizedContent !== ''}
        onCancel={() => setSummarizedContent('')}
        onUse={edited => {
          // Update the in-modal content preview
          setGeneratedContentModal(prev => ({ ...prev, content: edited }))

          // Persist edited content to the selected topic's generatedContent
          setContent(prev => {
            const next = { ...prev }
            const lesson = generatedContentModal.lesson
            const title = generatedContentModal.title
            if (lesson && title) {
              if (!next[lesson]) next[lesson] = {}
              if (!next[lesson][title]) next[lesson][title] = {}
              next[lesson][title].generatedContent = edited
            }
            return next
          })

          // Close summarized modal and notify
          setSummarizedContent('')
          setSummarizeModal({
            isVisible: false,
            instructions: '',
            isSummarizing: false
          })
          message.success('Edited content applied!')
        }}
        onApplyAll={handleApplyAllFromSummarizedClick}
        summarizedContent={summarizedContent}
        originalContentLength={generatedContentModal.content.length}
        isApplyingAll={isApplyingAll}
        onContentChange={value => setSummarizedContent(value)}
        onUndo={() => {
          // Close summarized content modal and summarization modal, keep generated content modal visible
          setSummarizedContent('')
          setSummarizeModal({
            isVisible: false,
            instructions: '',
            isSummarizing: false
          })
          setGeneratedContentModal(prev => ({ ...prev, isVisible: true }))
        }}
      />

      <ApplyAllConfirmModal
        visible={isApplyAllConfirmVisible}
        onCancel={() => setIsApplyAllConfirmVisible(false)}
        onConfirm={handleApplyAllConfirmClick}
        instructions={summarizeModal.instructions}
        isApplyingAll={isApplyingAll}
      />

      <ImagesModal
        visible={imagesModal.isVisible}
        onCancel={() =>
          setImagesModal({
            isVisible: false,
            imgArray: []
          })
        }
        imgArray={imagesModal.imgArray}
      />
    </div>
  )
}

export default TableOfContents
